import React, { useCallback, useState, useMemo } from 'react';
import { getLocaleDateTimeString, history, toTitleCase } from '../../utils';
import { Card } from '@mui/material';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import {
  Filters,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import useUrlState from '../../hooks/use-url-state';
import type { UnifiedTransaction } from '../../hooks/users/query/V2';

interface UserTransactionsProps {
  transactions: UnifiedTransaction[];
  searchText: string;
  onSearchChange: (search: string) => void;
  isLoading?: boolean;
  isFetching?: boolean;
}

const UserTransactions = ({
  transactions,
  searchText,
  onSearchChange,
  isLoading = false,
  isFetching = false,
}: UserTransactionsProps) => {
  const [filteredTransactions, setFilteredTransactions] =
    useState<UnifiedTransaction[]>(transactions);
  const [dateFilters, setDateFilters] = useState<Filters>({} as Filters);
  const [exportData, setExportData] = useState<boolean>(false);

  const [pageState, setPageState] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    pageNumber: 0,
    sortData: {
      key: 'created',
      direction: 'desc',
    } as ISortConfig,
  });

  // Update filtered transactions when props change
  useMemo(() => {
    setFilteredTransactions(transactions);
  }, [transactions]);

  const handleFiltersChange = useCallback(
    (filters: Filters): void => {
      const endDate = filters?.endDate || null;
      const startDate = filters?.startDate || null;
      const filtered = transactions.filter((transaction) => {
        const transactionDate = new Date(transaction.created).toISOString();
        const parsedTransactionDate = Date.parse(transaction.created);

        if (startDate && endDate) {
          return (
            !isNaN(parsedTransactionDate) &&
            new Date(startDate).toISOString() <= transactionDate &&
            transactionDate <= new Date(endDate).toISOString()
          );
        } else if (startDate) {
          return (
            !isNaN(parsedTransactionDate) &&
            new Date(startDate).toISOString() <= transactionDate
          );
        } else if (endDate) {
          return (
            !isNaN(parsedTransactionDate) &&
            transactionDate <= new Date(endDate).toISOString()
          );
        } else {
          return !isNaN(parsedTransactionDate);
        }
      });
      setDateFilters(filters);
      setFilteredTransactions(filtered);
    },
    [transactions]
  );

  const userColumns = [
    {
      id: 'created',
      label: 'Created',
      format: (row: UnifiedTransaction) => getLocaleDateTimeString(row.created),
    },
    {
      id: 'description',
      label: 'Description',
      searchable: true,
    },
    {
      id: 'type',
      label: 'Type',
      format: (row: UnifiedTransaction) => toTitleCase(row.type),
      searchable: true,
    },
    {
      id: 'amount',
      label: 'Amount',
      searchable: true,
    },
    {
      id: 'status',
      label: 'Status',
      format: (row: UnifiedTransaction) =>
        row.status == null || row.status === '' ? (
          <SeverityPill color={'warning'}>Transfer</SeverityPill>
        ) : (
          <SeverityPill
            color={
              row.status === 'failed'
                ? 'error'
                : row.status === 'requires_capture'
                ? 'warning'
                : 'success'
            }
          >
            {toTitleCase(row.status?.replace(/_/g, ' '))}
          </SeverityPill>
        ),
      searchable: true,
    },
  ];

  return (
    <div>
      <Card>
        <PaginationTable
          dataId="transactions"
          showFilter={false}
          isLoading={isLoading}
          isLoadingCache={isFetching}
          localSearch={false}
          noRemoteSort
          disableHasMore
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
          filters={dateFilters}
          onRowCLick={(id: string) => history.push(`/billing/transactions/detail/${id}`)}
          columns={userColumns}
          items={filteredTransactions}
          onSortChange={(sort) => {
            setPageState((prev) => ({
              ...prev,
              pageNumber: 0,
              sizePerPage: DEFAULT_ROWS_PER_PAGE,
              sortData: {
                key: sort.key,
                direction: sort.direction as 'asc' | 'desc',
              },
            }));
          }}
          sortBy={pageState.sortData?.key || 'created'}
          onPageChange={(page, newPage) => {
            setPageState((prev) => ({
              ...prev,
              pageNumber: newPage,
            }));
          }}
          onRowsPerPageChange={(pageSizeString) =>
            setPageState((prev) => ({
              ...prev,
              sizePerPage: Number(pageSizeString.target.value),
            }))
          }
          page={pageState.pageNumber}
          rowsPerPage={pageState.sizePerPage}
          padding={{ pt: 1, pl: 0, pr: 1 }}
          searchText={searchText}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPageState((prev) => ({
              ...prev,
              pageNumber: 0,
            }));
            onSearchChange(e.target.value);
          }}
          searchPlaceholder={'Search Transactions'}
          exportData={exportData}
          onExport={() => setExportData(false)}
          csvFileName={'transactions.csv'}
          onDateFilterChange={handleFiltersChange}
        />
      </Card>
    </div>
  );
};

export default UserTransactions;
